import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { ArchiveDTO, ArchiveGroup, MeetingType } from "../api/types";
import { MEETING_TYPE_LABEL, formatArchiveTime, formatBytes, todayKey } from "../utils/format";
import { useToast } from "./Toast";

interface ArchiveListProps {
  /** 已按类型分组的数据（来自 /archives?grouped=1） */
  groups: ArchiveGroup[];
  loading: boolean;
  onPreview: (archive: ArchiveDTO) => void;
  /** 是否可编辑纪要（ADMIN 或 DEPT_LEAD） */
  canEdit?: boolean;
  /** 编辑保存后回调（刷新列表） */
  onChanged?: () => void;
}

const GROUP_ORDER: MeetingType[] = ["DAILY", "WEEKLY", "MONTHLY"];
const GROUP_ICON: Record<string, string> = { DAILY: "📅", WEEKLY: "📆", MONTHLY: "🗓️" };
const ICON_CLASS: Record<string, string> = { DAILY: "", WEEKLY: "weekly", MONTHLY: "monthly" };

const TYPE_FILTERS: { value: "all" | MeetingType; label: string }[] = [
  { value: "all", label: "全部类型" },
  { value: "DAILY", label: "日会" },
  { value: "WEEKLY", label: "周会" },
  { value: "MONTHLY", label: "月会" },
];

export function ArchiveList({ groups, loading, onPreview, canEdit, onChanged }: ArchiveListProps) {
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | MeetingType>("all");
  const [editing, setEditing] = useState<ArchiveDTO | null>(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const { show } = useToast();
  const today = todayKey();

  const openEdit = (a: ArchiveDTO) => {
    setSaving(false);
    void api.archives
      .get(a.id)
      .then((full) => {
        setEditText(full.content ?? "");
        setEditing(full);
      })
      .catch((err: unknown) => show(err instanceof Error ? err.message : "纪要加载失败"));
  };

  const saveEdit = () => {
    if (!editing) return;
    if (!editText.trim()) {
      show("纪要正文不能为空");
      return;
    }
    setSaving(true);
    void api.archives
      .update(editing.id, editText)
      .then((saved) => {
        setEditing(null);
        show(saved.driveReady ? "纪要已保存并同步至微盘" : "纪要已保存");
        onChanged?.();
      })
      .catch((err: unknown) => show(err instanceof Error ? err.message : "保存失败"))
      .finally(() => setSaving(false));
  };

  const filteredGroups = useMemo(() => {
    const kw = keyword.trim();
    return groups
      .filter((g) => typeFilter === "all" || g.type === typeFilter)
      .map((g) => ({
        ...g,
        items: kw ? g.items.filter((a) => a.name.includes(kw) || a.deptName.includes(kw)) : g.items,
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, keyword, typeFilter]);

  const total = filteredGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <section className="panel" id="secArchive">
      <div className="panel-head">
        <div className="panel-title">会议纪要归档</div>
        <span className="ai-badge">🤖 AI 自动命名归档</span>
      </div>
      <div className="panel-body">
        <div className="archive-toolbar">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索部门 / 文件名…"
            aria-label="搜索会议纪要"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as "all" | MeetingType)}
            aria-label="按类型筛选纪要"
          >
            {TYPE_FILTERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <button className="icon-btn pad-lg" onClick={() => show("归档清单已导出为 Excel（演示）")}>
            导出
          </button>
        </div>
        <div className="crumb">
          📁 企业微信微盘 / <b>会议纪要</b> / 按月自动归档
        </div>

        {loading ? (
          <div className="loading-box">
            <span className="spinner" />
            正在加载纪要…
          </div>
        ) : total === 0 ? (
          <div className="empty-note">未找到匹配的纪要</div>
        ) : (
          <div>
            {[...filteredGroups]
              .sort((a, b) => GROUP_ORDER.indexOf(a.type as MeetingType) - GROUP_ORDER.indexOf(b.type as MeetingType))
              .map((g) => (
                <div key={g.type}>
                  <div className="archive-group-title">
                    {GROUP_ICON[g.type] ?? "📄"} {MEETING_TYPE_LABEL[g.type] ?? g.type}归档{" "}
                    <span className="count">{g.items.length} 份</span>
                  </div>
                  {g.items.map((a: ArchiveDTO) => (
                    <div className="archive-item" key={a.id}>
                      <div className={`file-icon ${ICON_CLASS[a.type] ?? ""}`} aria-hidden="true">
                        📄
                      </div>
                      <div className="fname">
                        <div className="name" title={a.name}>
                          {a.name}
                          {a.bizDate === today && <i className="new-dot">NEW</i>}
                          {a.editedAt && <i className="new-dot">已编辑</i>}
                        </div>
                        <div className="meta">
                          {formatArchiveTime(a.createdAt)} · {formatBytes(a.sizeBytes)} ·{" "}
                          {a.driveReady ? "已同步至企业微信微盘" : "本地正文（未同步微盘）"}
                        </div>
                      </div>
                      <div className="actions">
                        {canEdit && (
                          <button className="icon-btn" onClick={() => openEdit(a)}>
                            编辑
                          </button>
                        )}
                        <button className="icon-btn" onClick={() => onPreview(a)}>
                          预览
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => {
                            if (!a.driveReady) {
                              show("当前为演示模式：未配置企业微信微盘，无真实文件可下载");
                              return;
                            }
                            void api.archives
                              .download(a.id, a.name)
                              .then(() => show(`已开始下载：${a.name}.md`))
                              .catch((err: unknown) =>
                                show(err instanceof Error ? err.message : "下载失败"),
                              );
                          }}
                        >
                          下载
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>

      {editing && (
        <div
          className="modal-mask show"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <div className="modal" role="dialog" aria-modal="true" aria-label={editing.name}>
            <div className="modal-head">
              <span>编辑纪要</span>
              <button className="modal-close" onClick={() => setEditing(null)} aria-label="关闭">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="archive-edit-name">
                {editing.name}
              </div>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="在此填写会议实际内容（支持 markdown），保存后自动同步到企业微信微盘…"
                aria-label="纪要正文"
                className="archive-edit-area"
              />
              <div className="meta mt-6">
                支持 markdown；保存后
                {editing.driveReady ? "自动重新上传并同步微盘" : "仅存本地正文"}
              </div>
            </div>
            <div className="modal-foot">
              <button className="icon-btn" onClick={() => setEditing(null)} disabled={saving}>
                取消
              </button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>
                {saving ? "保存中…" : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
